import {get, isEmpty, isNil} from 'lodash'
import FinancialHelper, {getCurrency, getCurrencySymbol} from 'helpers/financial'
import {accountingTypes, permissions} from 'helpers/constants'
import {userHasPermission} from 'helpers/user'
import types from 'actions/types'
import {ACTION_STRINGS} from '../strings'
import {getSearchTerm, unCamelCase} from 'helpers/string'
import {PersonTypes, QuoteStatus} from 'helpers/quotes'
import iconNames from 'theme/icons'
import Icon from 'components/icon'
import React from 'react'
import {isPrintTemplatePublished} from 'helpers/printTemplates'
import {PrintTemplateType} from 'helpers/printTemplates/enum'
import {updateParamsForOrderType} from "helpers/opportunities"
import {isProOnlyAccountsIntegrationEnabled} from "helpers/integrations"

export const InvoiceType = {
  standard: 0,
  recurring: 1,
  batch: 2,
}

export const InvoiceCreateType = {
  ALL_ITEMS: 'ALL_ITEMS',
  ALL_REMAINING_ITEMS: 'ALL_REMAINING_ITEMS',
  ALL_REMAINING_ITEMS_FIXED_PERCENTAGE: 'ALL_REMAINING_ITEMS_FIXED_PERCENTAGE',
  TOTAL_REMAINING_CONTRACT_VALUE: 'TOTAL_REMAINING_CONTRACT_VALUE',
  ITEMS_ON_A_DELIVERY_NOTE: 'ITEMS_ON_A_DELIVERY_NOTE',
  REMAINING_ITEMS_RECEIVED_ON_PO: 'REMAINING_ITEMS_RECEIVED_ON_PO',
  FIXED_PRICE: 'FIXED_PRICE',
}

export const InvoiceFixedPriceAdjustment = {
  none: 0,
  manual: 1,
  automatic: 2,
  zero: 3,
}

export const InvoiceRecurringStatus = {
  DELETED: {id: 0, label: 'Deleted'},
  LIVE: {id: 1, label: 'Live'},
  ON_HOLD: {id: 2, label: 'On Hold'},
  CANCELLED: {id: 3, label: 'Cancelled'},
}

export const Posted_Invoice_Status = {
  NOT_POSTED: '-1',
  PENDING: '1',
  POSTED: '2',
  FAILED_TO_POST: '3',
}

export const InvoiceStatus = {
  EVERYTHING: {id: 4, title: 'Everything'},
  AWAITING_POSTING: {id: 1, title: 'Awaiting Posting'},
  ALL_INVOICES: {id: 2, title: 'All Invoices'},
  ALL_CREDIT_NOTES: {id: 3, title: 'All Credit Notes'},
}

export const getReadOnlyReason = (invoice, user) => {
  if (invoice.posted)
    return 'Posted invoices cannot be changed'
  if (invoice.type === InvoiceType.recurring)
    return 'To edit this invoice, please log into your Clarity Pro/Enterprise system'
  if (!userHasPermission(user, permissions.EDIT_INVOICES))
    return 'You do not have permission to edit this invoice'
  return null
}

export const canEditInvoice = (invoice, user) => {
  const postStatus = get(invoice, 'postStatus.value', "")

  return !isNil(invoice) &&
    !invoice.posted &&
    !(postStatus === Posted_Invoice_Status.PENDING) &&
    invoice.type !== InvoiceType.recurring &&
    invoice.type !== InvoiceType.batch &&
    userHasPermission(user, permissions.EDIT_INVOICES)
}

export const canRepostInvoice = (invoice, user) => {
  const postStatus = get(invoice, 'postStatus.value', "")

  return !isNil(invoice) &&
    !(postStatus === Posted_Invoice_Status.PENDING) &&
    invoice.type !== InvoiceType.recurring &&
    invoice.type !== InvoiceType.batch &&
    userHasPermission(user, permissions.EDIT_INVOICES)
}

export const isUnmarkPostedEnabled = (invoice, user) => {
  return !isNil(invoice) &&
    invoice.type !== InvoiceType.recurring &&
    invoice.type !== InvoiceType.batch &&
    userHasPermission(user, permissions.EDIT_INVOICES)
}

// Note: including add, edit, delete, re-order, etc.
export const canEditInvoiceItems = (invoice, user) => {
  return canEditInvoice(invoice, user)
}

export const getRecurringStatus = (id) => {
  const ret = Object.values(InvoiceRecurringStatus)
    .find(type => type.id === id)
  return ret || InvoiceRecurringStatus.LIVE
}

export const getInvoiceType = (type) => {
  let invoiceType
  switch (type) {
    case InvoiceType.standard:
      invoiceType = 'Standard'
      break
    case InvoiceType.recurring:
      invoiceType = 'Recurring'
      break
    case InvoiceType.batch:
      invoiceType = 'Batch'
      break
    default:
      invoiceType = 'Not Specified'
      break
  }
  return invoiceType
}

export const getEstimatorName = (order) => {
  if (order !== null && order !== undefined) {
    if (order.estimator) {
      return order.estimator.forename + ' ' + order.estimator.surname
    }
  } else {
    return ''
  }
}

export const isCreditNote = (invoice) => {
  return get(invoice, 'creditNote', false)
}

export const getColorBasedOnCreditNote = (muiTheme, creditNote) => {
  if (isNil(creditNote)) {
    return ''
  } else {
    return (creditNote === true || creditNote === 1)
      ? muiTheme.palette.invoiceType.creditNote
      : muiTheme.palette.invoiceType.invoice
  }
}

export const filterDeletedInvoiceItems = (invoiceItems) => {
  if (!isNil(invoiceItems)) {
    return invoiceItems.filter((invoiceItem) => {
      return invoiceItem.deleted !== true
    })
  } else {
    return []
  }
}

// Used in selected invoice reducer and sagas for undo...
export const invoiceChangeActions = [
  types.INVOICING_INVOICE_UPDATE_PROPERTY,
  types.INVOICING_INVOICE_ADD_ORDER_ITEM,
  types.INVOICING_INVOICE_ADD_ORDER_ITEM_AT_INDEX,
  types.INVOICING_INVOICE_EDIT_ORDER_ITEM,
  types.INVOICING_INVOICE_DELETE_ORDER_ITEM,
  types.INVOICING_INVOICE_DELETE_ORDER_ITEMS,
  types.INVOICING_INVOICE_COPY_ORDER_ITEM,
  types.INVOICING_INVOICE_COPY_ORDER_ITEMS,
  types.INVOICING_INVOICE_REORDER_ORDER_ITEM,
]

export const getCalculatedInvoiceItem = (item) => {
  if (!isNil(item)) {
    const quantity = FinancialHelper.getFloat(item.quantity)
    const unitCost = FinancialHelper.getFloat(item.unitCost)
    const unitPrice = FinancialHelper.getFloat(item.unitPrice)

    // Calculate totals
    item.totalCost = FinancialHelper.getFloat(unitCost * quantity)
    item.totalPrice = FinancialHelper.getFloat(unitPrice * quantity)

    return item
  }
}

const getInvoiceBasePDFUrl = (
  attachmentId,
  invoice,
  letterHead,
  eoriNumberEU,
  eoriNumberNI,
  printQuantity
) => {
  if (isNil(attachmentId) || isNil(invoice)) {
    throw new Error('Invalid argument')
  } else {
    // Note: This is the HTML, not the PDF
    let pdfUrl = `/attachments/${attachmentId}?invoice=${invoice.id}&preview=true`
    letterHead = (isNil(letterHead)) ? false : letterHead

    if (letterHead === true || letterHead === 'true') {
      pdfUrl = pdfUrl + '&letterHead=true'
    }

    if (eoriNumberEU === true) {
      pdfUrl = pdfUrl + '&showEoriNumberEU=true'
    }

    if (eoriNumberNI === true) {
      pdfUrl = pdfUrl + '&showEoriNumberNI=true'
    }

    if (printQuantity === true) {
      pdfUrl = pdfUrl + '&printQuantity=true'
    }

    return pdfUrl + '&ms=' + invoice.modifiedDate
  }
}
export const getInvoicePreviewPDFUrl = (
  attachmentId,
  invoice,
  letterHead,
  eoriNumberEU,
  eoriNumberNI,
  printQuantity
) =>
  getInvoiceBasePDFUrl(attachmentId, invoice, letterHead, eoriNumberEU, eoriNumberNI, printQuantity)

export const getInvoicePrintPDFUrl = (
  attachmentId,
  invoice,
  letterHead,
  eoriNumberEU,
  eoriNumberNI,
  printQuantity
) =>
  getInvoiceBasePDFUrl(attachmentId, invoice, letterHead, eoriNumberEU, eoriNumberNI, printQuantity)

export const getInvoiceDownloadPDFUrl = (
  attachmentId,
  invoice,
  letterHead,
  eoriNumberEU,
  eoriNumberNI,
  printQuantity
) =>
  getInvoiceBasePDFUrl(attachmentId, invoice, letterHead, eoriNumberEU, eoriNumberNI, printQuantity)

export const getInvoiceDownloadPDFName = (attachment, invoice) => {
  if (isNil(attachment) || isNil(invoice)) {
    throw new Error('Invalid argument')
  } else {
    return attachment.label + ' ' + invoice.ref
  }
}

export const getItemsDifferencePropertyKey = (current, previous) => current && Object.keys(current)
  .filter(v => (current && current.id === -1 || previous.id && previous.id === -1)
    ? ignoreKeysForNewItemComparison.indexOf(v) === -1 && current[v] !== previous[v]
    : current[v] !== previous[v]
  )


export const getLastUndoLabel = (changeLogInvoiceItems = []) => {
  let label = ACTION_STRINGS.UNDO_CHANGES
  if (changeLogInvoiceItems.length > 1) {
    const currentLogItem = changeLogInvoiceItems.slice(-1)[0]
    const previousToCurrentLogItem = changeLogInvoiceItems.slice(-2, -1)[0]

    if (currentLogItem.length === previousToCurrentLogItem.length) {
      for (let i = 0; i < Object.keys(currentLogItem).length; i++) {
        const compareResult = getItemsDifferencePropertyKey(currentLogItem[i], previousToCurrentLogItem[i])
        if (compareResult.length > 0) {
          label = `${ACTION_STRINGS.UNDO} ${unCamelCase(compareResult[0])}`
          break
        }
      }
    } else {
      label = `${ACTION_STRINGS.UNDO} ${(currentLogItem.length - previousToCurrentLogItem.length < 0)
        ? ACTION_STRINGS.DELETE
        : ACTION_STRINGS.ADD}`
    }
  }
  return label
}

export const filterLogItemsToDelete = (currentInvoiceItems, invoiceItems) => {
  const invoiceItemsIds = invoiceItems.map(item => item.id)
  return currentInvoiceItems
    .filter(item => !invoiceItemsIds.includes(item.id))
}

export const mapNegativeIdInvoiceItemsAgainstCurrent = (currentInvoiceItems, invoiceItems) =>
  invoiceItems.map(item => {
    if (currentInvoiceItems && currentInvoiceItems.length > 0 && item && item.id === -1) {
      const matchingCurrentItem = currentInvoiceItems.find(cItem => cItem.lineItemLetter === item.lineItemLetter)
      const diffKey = getItemsDifferencePropertyKey(matchingCurrentItem, item)[0]
      return {...matchingCurrentItem, [diffKey]: item[diffKey]}
    } else {
      return item
    }
  })

const ignoreKeysForNewItemComparison = [
  'id',
  '_editedKeys',
  'orderId',
  'departmentName',
  'invoiceId',
  'modifiedDate',
  'notes',
  'orderItemId',
  'taxCode',
  'netPrice',
]

export const calculateDue = (invoiceItem) => {
  if (invoiceItem.orderItem) {
    const orderItemTotalPrice = get(invoiceItem, 'orderItem.totalPrice', null)
    return (isNil(orderItemTotalPrice) || orderItemTotalPrice === 0)
      ? 0 : (invoiceItem.totalPrice / invoiceItem.orderItem.totalPrice) * 100
  }

  return null
}

export const getColorForInvoiceStatus = (muiTheme, status) => {
  switch (status) {
    case InvoiceStatus.AWAITING_POSTING.id:
      return muiTheme.palette.invoiceType.awaitingPosting
    case InvoiceStatus.ALL_INVOICES.id:
      return muiTheme.palette.invoiceType.invoice
    case InvoiceStatus.ALL_CREDIT_NOTES.id:
      return muiTheme.palette.invoiceType.creditNote
    default:
      return muiTheme.palette.invoiceType.invoice
  }
}

export const getInvoiceCreateTypeStatus = (invoiceCreateType) => {
  switch (invoiceCreateType) {
    case InvoiceCreateType.ALL_ITEMS:
      return 'All Items On The Job'
    case InvoiceCreateType.ALL_REMAINING_ITEMS:
      return 'All Remaining Items On The Job'
    case InvoiceCreateType.ALL_REMAINING_ITEMS_FIXED_PERCENTAGE:
      return 'All Remaining Items On The Job With A Fixed %'
    case InvoiceCreateType.TOTAL_REMAINING_CONTRACT_VALUE:
      return 'Total Remaining Contract Value Of The Job'
    case InvoiceCreateType.ITEMS_ON_A_DELIVERY_NOTE:
      return 'Items On A Delivery Note'
  }
}

export const canInvoiceBeforeDelivered = (user) =>
  user.settings.invoiceWithoutDeliveryNotes

export const getOrderStatusesCanInvoiceFrom = (user) => {
  const invoiceBeforeDelivered = canInvoiceBeforeDelivered(user)

  return [
    QuoteStatus.delivered,
    QuoteStatus.invoiced,
    QuoteStatus.paid,
    invoiceBeforeDelivered ? QuoteStatus.partDelivered : null,
    invoiceBeforeDelivered ? QuoteStatus.confirmed : null,
  ].filter(status => !isNil(status))
}


export const canInvoiceJob = (user, order) =>
  !isNil(order) && getOrderStatusesCanInvoiceFrom(user).includes(order.status.value)

export const getDeliveryCharges = (invoiceItems, key = 'unitPrice') => {
  return isEmpty(invoiceItems)
    ? 0
    : invoiceItems
      .filter(invoiceItem => invoiceItem.partCode === 'DELIVERY')
      .reduce((total, item) => total + item[key], 0)
}

export const getInvoicingFilterParams = (
  searchTerm,
  status,
  companyId,
  estimatorId,
  targetStart,
  targetEnd,
  orderTypeId) => {

  const params = {}

  if (getSearchTerm(searchTerm))
    params['searchTerm'] = searchTerm

  if (!isNil(status))
    params['status'] = status

  if (!isEmpty(companyId))
    params['companyId'] = companyId.map(option => option.value)

  if (!isEmpty(estimatorId))
    params['estimatorId'] = estimatorId

  updateParamsForOrderType(params, orderTypeId)

  if (!isNil(targetStart) && !isNil(targetEnd)) {
    params['invoicedStart'] = targetStart
    params['invoicedEnd'] = targetEnd
  }
  params['total'] = true
  return params
}

export const getSalesAnalysisInvoiceFilterParams = (
  search,
  statusTypes,
  companies,
  userTypes,
  contacts,
  targetStart,
  targetEnd,
  orderTypeId,
  userSettings) => {

  const params = {}

  if (getSearchTerm(search))
    params.search = search

  if (!isNil(statusTypes))
    params['status'] = statusTypes

  if (!isEmpty(companies))
    params['companyId'] = companies.map(company => company.value)

  if (!isEmpty(contacts)) {
    params['personId'] = contacts
    const allUserTypes = PersonTypes.map(type => type.value)

    if (!isEmpty(userTypes)) {
      userTypes.forEach(type => {
        params[type] = true
      })
      const nonSelectedUserTypes = allUserTypes.filter(filter => !userTypes.includes(filter))
      nonSelectedUserTypes.forEach(type => {
        params[type] = false
      })
    } else {
      allUserTypes.forEach(type => {
        params[type] = true
      })
    }

  }

  if (!isNil(targetStart) && !isNil(targetEnd)) {
    params['invoicedStart'] = targetStart
    params['invoicedEnd'] = targetEnd
  }

  updateParamsForOrderType(params, orderTypeId)

  const excludeHomeCompany = get(userSettings, 'excludeHomeCompanyFromReporting', false)

  params['showCompleted'] = true
  params['showInactive'] = true
  params['total'] = true
  params['excludeHomeCompany'] = excludeHomeCompany
  return params
}

export const getPurchaseInvoicingFilterParams = (
  searchTerm,
  status,
  companyId,
  creatorId,
  targetStart,
  targetEnd) => {

  const params = {}

  if (getSearchTerm(searchTerm))
    params['searchTerm'] = searchTerm

  if (!isNil(status))
    params['status'] = status

  if (!isEmpty(companyId))
    params['companyId'] = companyId.map(option => option.value)

  if (!isEmpty(creatorId))
    params['creatorId'] = creatorId

  if (!isNil(targetStart) && !isNil(targetEnd)) {
    params['issuedStart'] = targetStart
    params['issuedEnd'] = targetEnd
  }
  params.total = true
  return params
}

export const getQuickAddDefaults = (invoice, defaultNominalCode) => ({
  id: -1,
  quantity: 1,
  unitPrice: 0,
  unitCost: 0,
  netPrice: 0,
  totalPrice: 0,
  totalCost: 0,
  partCode: 'ITEM',
  desc: 'Item',
  nominalCode: defaultNominalCode,
  taxCode: invoice ? invoice.taxCode : null,
  taxRate: invoice ? invoice.taxRate : 0,
  taxCodeId: invoice ? invoice.taxCodeId : null,
})

export const createDeliveryItem = (invoice, deliveryCost, defaultNominalCode) => {
  return {
    ...getQuickAddDefaults(invoice, defaultNominalCode),
    desc: 'Delivery Charge',
    partCode: 'DELIVERY',
    unitPrice: deliveryCost,
    totalPrice: deliveryCost,
  }
}

export const getInvoiceTypeName = (isCreditNote) => {
  return isCreditNote ? 'Credit Note' : 'Invoice'
}

export const getPostedIconName = (invoice) => {
  const integrationInvoiceWasPostedTo = accountingTypes.forId(get(invoice, 'postedTo.id'))
  const integrationIcon = get(integrationInvoiceWasPostedTo, 'icon', null)

  return isNil(integrationIcon)
    ? iconNames.tick
    : integrationIcon
}

export const getPostedIcon = (invoice) => {
  const isPosted = get(invoice, 'posted', false)
  if (!isPosted)
    return null

  const iconStyle = {
    margin: 0,
    position: 'absolute',
    marginLeft: -6,
  }

  return <Icon
    name={getPostedIconName(invoice)}
    size={16}
    style={iconStyle}
    type={'ORIGINAL'}/>
}

export const getPrintTemplatesForInvoice = (invoice, printTemplates) => {
  if (isNil(printTemplates)) {
    throw new Error('Invalid argument')

  } else {

    const type = isCreditNote(invoice)
      ? PrintTemplateType.CREDIT_NOTE
      : PrintTemplateType.INVOICE

    return printTemplates
      .filter(isPrintTemplatePublished)
      .filter(a => a.type === type)
  }
}

export const getInvoiceOption = (invoice) => {
  return isNil(invoice)
    ? null
    : {
      value: invoice.id,
      label: `${invoice.ref}: ${invoice.desc}`,
      invoice,
      status: get(invoice, 'status.label', ''),
      className: `invoice-${get(invoice, 'status.label', '').toLowerCase()}`,
    }
}

export const isAddInvoiceTypeKnown = (data, order) => {
  const creditNote = get(data, 'creditNote', null)
  const invoicedValue = get(order, 'summaryinfo.invoicedValue', null)
  return !isNil(creditNote) || invoicedValue === 0
}

export const isAddingInvoice = (data, order) => {
  const creditNote = get(data, 'creditNote', false)
  const invoicedValue = get(order, 'summaryinfo.invoicedValue', null)
  return !creditNote || invoicedValue === 0
}

export const isAdjustAutomaticallyEnabled = (totalLineItemValue) => {
  return totalLineItemValue !== 0
}

export const getCurrencyFieldOptionsForInvoices = (
  currencyId,
  currencies = [],
  accuracy = 2,
  isPrepending = false
) => {
  if (isNil(currencyId)) return null
  const currency = getCurrency(currencyId, currencies)
  const currencySymbol = getCurrencySymbol(currencyId, currencies)

  const options = {
    accuracy,
    step: 5,
  }

  if (isPrepending) {
    options.prepend = FinancialHelper.getUnicodeCharacter(currency.unicodePattern)
  } else {
    options.append = currencySymbol
  }

  return options
}

export const isPostedToProAccountingPackage = (invoice) => {
  const postedTo = accountingTypes.forId(get(invoice, 'postedTo.id'))
  return get(postedTo, 'proOnly', false)
}

export const isPostedToGoAccountingPackage = (invoice) => {
  return get(invoice, 'posted')
    && get(invoice, 'postedTo.id')
    && !isPostedToProAccountingPackage(invoice)
}

export const isPostedToAnyAccountingPackage = (invoice) => {
  return isPostedToProAccountingPackage(invoice) || isPostedToGoAccountingPackage(invoice)
}

export const isMarkedPosted = (invoice) => {
  return get(invoice, 'posted') && !isPostedToAnyAccountingPackage(invoice)
}

export const canSelectInvoice = (invoice, accountsIntegration) => {
  const isPostedToGo = isPostedToGoAccountingPackage(invoice)
  const isProOnly = isProOnlyAccountsIntegrationEnabled(accountsIntegration)
  return !(isPostedToGo && isProOnly)
}

export const getInvoicePostingPromptMessage = (hasAlreadyPostedInvoices, accountsIntegration) => {
  let message = `Post selected invoice(s) to ${accountsIntegration.title}?`
  if (hasAlreadyPostedInvoices) {
    message += ` One or more of the selected invoices has already been posted and will be reposted.`
  }
  return message
}

export default {
  getInvoiceType,
  getEstimatorName,
  getColorBasedOnCreditNote,
  InvoiceType,
  InvoiceStatus,
  InvoiceFixedPriceAdjustment,
  InvoiceRecurringStatus,
  invoiceChangeActions,
  canEditInvoice,
  canRepostInvoice,
  canEditInvoiceItems,
  filterDeletedInvoiceItems,
  getReadOnlyReason,
  getRecurringStatus,
  getCalculatedInvoiceItem,
  getInvoiceBasePDFUrl,
  getInvoicePreviewPDFUrl,
  getInvoicePrintPDFUrl,
  getInvoiceDownloadPDFUrl,
  getInvoiceDownloadPDFName,
  getCurrencyFieldOptionsForInvoices,
  isPostedToProAccountingPackage,
  isPostedToGoAccountingPackage,
  isPostedToAnyAccountingPackage,
  isMarkedPosted,
  canSelectInvoice,
}
